'use client';

import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Call, Device } from '@twilio/voice-sdk';
import { fetchRecruiterCallVoiceToken } from '@hooks/useSwrApi/endpoints/recruiter-call';
import type { RecruiterCallConnectParams } from '@type/recruiter-call';

export type TwilioDeviceState = 'idle' | 'registering' | 'ready' | 'connecting' | 'in-call' | 'error';

interface UseTwilioDeviceReturn {
  state: TwilioDeviceState;
  error: string | null;
  callSid: string | null;
  isMuted: boolean;
  callDuration: number;
  connect: (params: RecruiterCallConnectParams) => Promise<void>;
  disconnect: () => void;
  toggleMute: () => void;
  /** Audio output device picker state — see hook docstring. */
  outputDevices: MediaDeviceInfo[];
  selectedOutputDeviceId: string | null;
  isOutputSelectionSupported: boolean;
  selectOutputDevice: (deviceId: string | null) => Promise<void>;
  testOutputDevice: () => Promise<void>;
}

// Persisted across sessions — Chrome's per-origin deviceId is stable until the
// user clears cookies, so a saved USB-headset id will still resolve tomorrow.
const OUTPUT_DEVICE_STORAGE_KEY = 'bj:recruiter-call:speaker-device-id';

/**
 * Bootstraps a Twilio.Device for the logged-in recruiter and exposes the
 * softphone lifecycle as React state.
 *
 * The Device is created lazily on first `connect()` and torn down on
 * component unmount so we don't keep an idle WebRTC session open per
 * candidate profile tab.
 *
 * ## Audio output device selection
 *
 * By default the Twilio Voice JS SDK plays incoming audio through the
 * browser's default output device — which on Windows is very often the
 * laptop speakers, NOT the USB headset the recruiter's mic is on. Result:
 * they speak into the headset (mic works, candidate hears them, recording
 * captures both sides) but hear silence during the call because playback
 * goes to muted / docked / turned-off speakers. Diagnosed 2026-08-14 from
 * a support ticket where the recruiter tried different headsets in vain.
 *
 * This hook exposes {@link UseTwilioDeviceReturn.selectOutputDevice} so a
 * settings UI can pin playback (both call audio and ringtone) to a specific
 * device, persisted per-origin in localStorage. On the next session the
 * saved deviceId is re-applied after `device.register()`.
 *
 * Devices enumerated via `navigator.mediaDevices.enumerateDevices()` are
 * unlabeled until mic permission is granted; labels populate automatically
 * once the first `connect()` triggers Twilio's `getUserMedia` prompt.
 */
export const useTwilioDevice = (): UseTwilioDeviceReturn => {
  const [state, setState] = useState<TwilioDeviceState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [callSid, setCallSid] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const [outputDevices, setOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedOutputDeviceId, setSelectedOutputDeviceIdState] = useState<string | null>(null);
  const [isOutputSelectionSupported, setIsOutputSelectionSupported] = useState(true);

  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Held in a ref (not just state) so `ensureDevice` — which is only
  // recreated on mount — reads the current value at register time.
  const selectedOutputDeviceIdRef = useRef<string | null>(null);

  const clearDurationTimer = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    clearDurationTimer();
    if (callRef.current) {
      callRef.current.disconnect();
      callRef.current = null;
    }
    if (deviceRef.current) {
      deviceRef.current.destroy();
      deviceRef.current = null;
    }
    setCallSid(null);
    setIsMuted(false);
    setCallDuration(0);
  }, [clearDurationTimer]);

  useEffect(() => teardown, [teardown]);

  // Load the saved output device preference + populate the picker on mount.
  // `enumerateDevices()` returns unlabeled entries pre-permission; labels
  // fill in once Device registration triggers the mic prompt.
  useEffect(() => {
    let cancelled = false;

    try {
      const saved = window.localStorage.getItem(OUTPUT_DEVICE_STORAGE_KEY);
      if (saved) {
        selectedOutputDeviceIdRef.current = saved;
        setSelectedOutputDeviceIdState(saved);
      }
    } catch { /* localStorage may be blocked (Safari private mode, etc.) */ }

    const refresh = async () => {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
        return;
      }
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        setOutputDevices(devices.filter((d) => d.kind === 'audiooutput'));
      } catch {
        if (!cancelled) setOutputDevices([]);
      }
    };
    void refresh();

    const nmd = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
    nmd?.addEventListener?.('devicechange', refresh);
    return () => {
      cancelled = true;
      nmd?.removeEventListener?.('devicechange', refresh);
    };
  }, []);

  const applySpeakerSelection = useCallback(async (device: Device, deviceId: string | null): Promise<void> => {
    if (!device.audio) return;
    setIsOutputSelectionSupported(device.audio.isOutputSelectionSupported);
    if (!device.audio.isOutputSelectionSupported || !deviceId) return;
    if (!device.audio.availableOutputDevices.has(deviceId)) return;
    try {
      await device.audio.speakerDevices.set(deviceId);
      await device.audio.ringtoneDevices.set(deviceId);
    } catch {
      // Device disappeared between the availability check and the set call
      // (unplugged headset, etc.). Fall back silently to the default.
    }
  }, []);

  const ensureDevice = useCallback(async (): Promise<Device> => {
    if (deviceRef.current) {
      return deviceRef.current;
    }
    setState('registering');
    const { token } = await fetchRecruiterCallVoiceToken();
    if (!token) {
      throw new Error('RecruiterCall: voice-token endpoint returned no token');
    }
    const device = new Device(token, {
      // logLevel: 'debug', // enable when debugging WebRTC issues
    });
    device.on('error', (twilioError: Error) => {
      setError(twilioError.message);
      setState('error');
    });
    await device.register();
    // Now that `device.audio.availableOutputDevices` is populated (mic
    // permission is granted as a side effect of register), re-emit the list
    // with labels + apply the saved selection.
    if (device.audio) {
      setOutputDevices(Array.from(device.audio.availableOutputDevices.values()));
      await applySpeakerSelection(device, selectedOutputDeviceIdRef.current);
    }
    deviceRef.current = device;
    setState('ready');
    return device;
  }, [applySpeakerSelection]);

  const attachCallListeners = useCallback((call: Call) => {
    call.on('accept', () => {
      setState('in-call');
      setCallSid(call.parameters.CallSid ?? null);
      const startedAt = Date.now();
      clearDurationTimer();
      durationTimerRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - startedAt) / 1000));
      }, 1000);
    });
    call.on('disconnect', () => {
      clearDurationTimer();
      callRef.current = null;
      setState('ready');
    });
    call.on('cancel', () => {
      clearDurationTimer();
      callRef.current = null;
      setState('ready');
    });
    call.on('error', (twilioError: Error) => {
      clearDurationTimer();
      setError(twilioError.message);
      setState('error');
    });
    call.on('mute', (muted: boolean) => setIsMuted(muted));
  }, [clearDurationTimer]);

  const connect = useCallback(async (params: RecruiterCallConnectParams) => {
    setError(null);
    try {
      const device = await ensureDevice();
      setState('connecting');
      const call = await device.connect({ params });
      callRef.current = call;
      attachCallListeners(call);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setState('error');
    }
  }, [attachCallListeners, ensureDevice]);

  const disconnect = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect();
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (!callRef.current) return;
    const next = !callRef.current.isMuted();
    callRef.current.mute(next);
  }, []);

  const selectOutputDevice = useCallback(async (deviceId: string | null): Promise<void> => {
    selectedOutputDeviceIdRef.current = deviceId;
    setSelectedOutputDeviceIdState(deviceId);
    try {
      if (deviceId) {
        window.localStorage.setItem(OUTPUT_DEVICE_STORAGE_KEY, deviceId);
      } else {
        window.localStorage.removeItem(OUTPUT_DEVICE_STORAGE_KEY);
      }
    } catch { /* localStorage blocked — the ref still drives the current session */ }
    if (deviceRef.current) {
      await applySpeakerSelection(deviceRef.current, deviceId);
    }
  }, [applySpeakerSelection]);

  const testOutputDevice = useCallback(async (): Promise<void> => {
    // Twilio plays a short beep through the currently-configured output
    // device. Requires the Device to be registered — but registering is
    // the side effect that grants mic permission + populates device
    // labels, so we do it on-demand here rather than eagerly on modal
    // open (an unprompted mic dialog would feel invasive). Users who
    // click Test before ever placing a call see the permission prompt
    // in the context they expect, then hear the beep — and the device
    // dropdown fills in with real names for the follow-up call.
    try {
      const device = await ensureDevice();
      await device.audio?.speakerDevices.test();
    } catch {
      // Non-fatal; usually a codec/autoplay quirk on the test tone itself,
      // or a mic-permission denial (already surfaced via `state === 'error'`).
    }
  }, [ensureDevice]);

  return {
    state,
    error,
    callSid,
    isMuted,
    callDuration,
    connect,
    disconnect,
    toggleMute,
    outputDevices,
    selectedOutputDeviceId,
    isOutputSelectionSupported,
    selectOutputDevice,
    testOutputDevice,
  };
};
